import * as Yup from "yup";

const doulaSchema = Yup.object().shape({
  expecting: Yup.string()
    .required("Please select if you are expecting")
    .oneOf(["Yes", "No"], "Please select a valid option"),

  kids: Yup.number()
    .min(0, "Number of kids cannot be negative")
    .max(20, "Number of kids must be less than 20"),

  motherName: Yup.string()
    .when("expecting", {
      is: "Yes",
      then: (schema) => schema.required("Mother’s name is required")
        .max(50, "Name must be less than 50 characters")
        .matches(/^[a-zA-Z\s]+$/, "Name must contain only letters and spaces")
        .transform((value) => value?.replace(/\s+/g, " ").trim()),
      otherwise: (schema) => schema.notRequired().nullable()
    }),

  isBookingForSomeoneElse: Yup.string()
    .when("expecting", {
      is: "No",
      then: (schema) => schema.required("Please select if you are booking for someone else")
        .oneOf(["Yes", "No"], "Please select a valid option"),
      otherwise: (schema) => schema.notRequired().nullable()
    }),

  relationship: Yup.string()
    .when("isBookingForSomeoneElse", {
      is: "Yes",
      then: (schema) => schema.required("Relationship with customer is required")
        .max(50, "Relationship must be less than 50 characters")
        .matches(/^[a-zA-Z\s]+$/, "Relationship must contain only letters and spaces")
        .transform((value) => value?.replace(/\s+/g, " ").trim()),
      otherwise: (schema) => schema.notRequired().nullable()
    }),

  expectingMotherName: Yup.string()
    .when("isBookingForSomeoneElse", {
      is: "Yes",
      then: (schema) => schema.required("Expecting mother's name is required")
        .max(50, "Name must be less than 50 characters")
        .matches(/^[a-zA-Z\s]+$/, "Name must contain only letters and spaces")
        .transform((value) => value?.replace(/\s+/g, " ").trim()),
      otherwise: (schema) => schema.notRequired().nullable()
    }),

  expectingMotherPhone: Yup.string()
    .when("isBookingForSomeoneElse", {
      is: "Yes",
      then: (schema) => schema.required("Phone number is required")
        .matches(/^[0-9\s+\-()]+$/, "Please enter a valid phone number")
        .min(10, "Phone number must be at least 10 digits")
        .max(11, "Phone number must be less than 11 digits")
        .transform((value) => value?.replace(/\s+/g, " ").trim()),
      otherwise: (schema) => schema.notRequired().nullable()
    }),

  dueDate: Yup.string()
    .when("expecting", {
      is: "Yes",
      then: (schema) =>
        schema
          .max(50, "Due date must be less than 50 characters")
          .transform((value) => value?.replace(/\s+/g, " ").trim()),
      otherwise: (schema) =>
        schema
          .nullable()
          .notRequired()
          .transform((value) => value?.replace(/\s+/g, " ").trim()),
    }),

  birthLocation: Yup.string()
    .trim()
    .required("Birth location is required")
    .max(100, "Birth location must be less than 100 characters")
    .transform((value) => value?.replace(/\s+/g, " ").trim())
    .test(
      "no-10-consecutive-numbers",
      "Invalid Address",
      (value) => {
        if (!value) return true; // other validators handle empty
        return !/\d{10}/.test(value); // FAIL if contains 10 digits in a row
      }),

  supportType: Yup.string()
    .required("Support type is required")
    .oneOf(["Prenatal", "Labor & Birth", "Postpartum"], "Please select a valid support type"),

  medicalConcerns: Yup.string()
    .max(200, "Medical concerns must be less than 200 characters")
    .transform((value) => value?.replace(/\s+/g, " ").trim()),

  partnerName: Yup.string()
    .max(50, "Name must be less than 50 characters")
    .matches(/^[a-zA-Z\s]+$/, "Name must contain only letters and spaces")
    .transform((value) => value?.replace(/\s+/g, " ").trim()),

  partnerInvolvement: Yup.string()
    .required("Please specify Partner's involvement")
    .oneOf(["Active", "Partial", "Minimal", "Not Involved"], "Please select a valid option"),

  // Emergency Contact 1 (all required)
  emergency1Name: Yup.string()
    .required("Please enter first emergency contact name")
    .max(50, "Name must be less than 50 characters")
    .matches(/^[a-zA-Z\s]+$/, "Name must contain only letters and spaces")
    .transform((value) => value?.replace(/\s+/g, " ").trim()),

  emergency1Phone: Yup.string()
    .required("Please enter first emergency contact phone number")
    .matches(/^[0-9\s+\-()]+$/, "Please enter a valid phone number")
    .min(10, "Phone number must be at least 10 digits")
    .max(11, "Phone number must be less than 11 digits")
    .transform((value) => value?.replace(/\s+/g, " ").trim()),

  emergency1Relation: Yup.string()
    .required("Please enter first emergency contact relation")
    .max(30, "Relationship must be less than 30 characters")
    .matches(/^[a-zA-Z\s]+$/, "Relationship must contain only letters and spaces")
    .transform((value) => value?.replace(/\s+/g, " ").trim()),

  emergency2Name: Yup.string()
    .max(50, "Name must be less than 50 characters")
    .matches(/^[a-zA-Z\s]*$/, "Name must contain only letters and spaces")
    .transform((value) => value?.replace(/\s+/g, " ").trim())
    .test('different-name', 'Emergency contact names cannot be the same', function (value) {
      if (!value) return true; // Not required, so empty is fine
      const emergency1Name = this.parent.emergency1Name;
      return value.toLowerCase() !== emergency1Name?.toLowerCase();
    }),

  emergency2Phone: Yup.string()
    .matches(/^[0-9\s+\-()]*$/, "Please enter a valid phone number")
    .min(10, "Phone number must be at least 10 digits")
    .max(11, "Phone number must be less than 11 digits")
    .transform((value) => value?.replace(/\s+/g, " ").trim())
    .test('different-phone', 'Emergency contact phones cannot be the same', function (value) {
      if (!value) return true; // Not required, so empty is fine
      const emergency1Phone = this.parent.emergency1Phone;
      // Remove non-digit characters for comparison
      const cleanPhone1 = emergency1Phone?.replace(/\D/g, '');
      const cleanPhone2 = value?.replace(/\D/g, '');
      return cleanPhone2 !== cleanPhone1;
    }),

  emergency2Relation: Yup.string()
    .max(30, "Relationship must be less than 30 characters")
    .matches(/^[a-zA-Z\s]*$/, "Relationship must contain only letters and spaces")
    .transform((value) => value?.replace(/\s+/g, " ").trim()),
});


export default doulaSchema;
